import React, { useState, useEffect } from 'react'
import { T, tierFromProb, RatingBadge, probColor, pct, Spinner, Empty } from '../nfl/ui'
import { getCFBTop } from './api'
import { propLine } from './cards'

// Parlay builder — pick legs from the week's top plays, see the combined model
// probability and a fair American price. Model math only (independent-leg product);
// clearly framed as research, not a recommended wager.

const probToAmerican = p => {
  if (!p || p <= 0 || p >= 1) return '—'
  const dec = 1 / p
  return dec >= 2 ? `+${Math.round((dec - 1) * 100)}` : `${Math.round(-100 / (dec - 1))}`
}

export function ParlayTab({ season, week, onPlayer }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [legs, setLegs] = useState({})   // key -> play
  useEffect(() => {
    let live = true; setLoading(true)
    getCFBTop(season, week, 40).then(d => { if (live) { setData(d); setLoading(false) } })
    return () => { live = false }
  }, [season, week])
  const key = p => `${p.cfbd_id}|${p.prop}`
  const toggle = p => setLegs(L => { const k = key(p); const n = { ...L }; if (n[k]) delete n[k]; else n[k] = p; return n })
  const chosen = Object.values(legs)
  const combined = chosen.reduce((a, p) => a * (p.prob || 0), 1)
  const plays = data?.top || []
  return (
    <div>
      {chosen.length > 0 && (
        <div style={{ background: T.card, border: `1px solid ${T.greenMid}`, borderRadius: 12, padding: 13, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{chosen.length}-leg parlay</span>
            <button onClick={() => setLegs({})} style={{ fontSize: 11, color: T.muted, background: 'none', border: 'none', cursor: 'pointer' }}>clear</button>
          </div>
          {chosen.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 12 }}>
              <span style={{ flex: 1, color: T.muted2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <b style={{ color: T.text }}>{p.name}</b> · {p.prop === 'anytime_td' ? 'Anytime TD' : `${p.market} O${propLine(p)}`}
              </span>
              <span style={{ fontFamily: T.mono, color: probColor(p.prob) }}>{pct(p.prob)}</span>
              <button onClick={() => toggle(p)} style={{ color: T.red, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>×</button>
            </div>
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
            <div style={{ background: T.card3, borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ fontSize: 9, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em' }}>Model win %</div>
              <div style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 18, color: probColor(combined) }}>{pct(combined)}</div>
            </div>
            <div style={{ background: T.card3, borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ fontSize: 9, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em' }}>Fair price</div>
              <div style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 18, color: T.text }}>{probToAmerican(combined)}</div>
            </div>
          </div>
          <div style={{ fontSize: 10, color: T.faint, marginTop: 8, lineHeight: 1.4 }}>
            Assumes independent legs — correlated legs (same game) will differ. Research, not a wager.
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em', margin: '4px 2px 8px' }}>Tap to add a leg</div>
      {loading ? <Spinner />
        : plays.length === 0 ? <Empty message={`No plays for week ${week} yet.`} />
        : plays.map((p, i) => {
          const on = !!legs[key(p)]
          const tier = tierFromProb(p.prob, p.confidence)
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', marginBottom: 7,
              background: T.card, border: `1px solid ${on ? T.green : T.border}`, borderRadius: 10, cursor: 'pointer' }}
              onClick={() => toggle(p)}>
              <RatingBadge tier={tier} size={26} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name} <span style={{ fontSize: 10, color: T.muted }}>{p.pos}</span></div>
                <div style={{ fontSize: 10.5, color: T.muted2 }}>{p.prop === 'anytime_td' ? 'Anytime TD' : `${p.market} O${propLine(p)}`} · {p.team}</div>
              </div>
              <span style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 14, color: probColor(p.prob) }}>{pct(p.prob)}</span>
              <span style={{ fontSize: 18, color: on ? T.green : T.faint, width: 18, textAlign: 'center' }}>{on ? '✓' : '+'}</span>
            </div>
          )
        })}
      <div style={{ height: 16 }} />
    </div>
  )
}
